import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useState } from "react";

import IconButton from "../../../elements/IconButton";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";

const TypesWithoutRefresh = ["Header", "Document"];

// Check for queries that contain this section's (widgets) ID.
// IF there are any, refetch the query, and disable the refresh button for 5 seconds.
export const RefreshQueryButton = ({
  section,
  textColor,
  viewID,
  recordID,
}: {
  section: any;
  textColor?: string;
  viewID?: string; // Provided when inside the ViewWidgetDrawer
  recordID?: string; // Provided when inside the ViewRecordDrawer
}) => {
  const tenantID = useAtomValue(tenantIDAtom);
  const baseQueryKey = [tenantID, "records", section?.ModuleAPIName];
  const isFetching = useIsFetching({ queryKey: baseQueryKey });
  const [refreshing, setRefreshing] = useState(false);
  const [refreshDisabled, setRefreshDisabled] = useState(false);
  const client = useQueryClient();

  return !TypesWithoutRefresh.includes(section?.Type) ? (
    <IconButton
      onClick={refreshQuery}
      icon="mdi mdi-refresh"
      style={{ color: textColor }}
      disabled={!!isFetching || refreshDisabled}
      loading={refreshing}
    />
  ) : null;

  function refreshFlowChart() {
    if (section?.ModuleAPIName && section?.FlowChartConfig) {
      return client.invalidateQueries({
        queryKey: ["SubViewFlow", section.ModuleAPIName, viewID, recordID],
      });
    }

    client.invalidateQueries({
      queryKey: [tenantID, "record", "Flows", section?.RecordID],
    });
  }

  async function refreshQuery() {
    if (section.Type === "FlowChart") return refreshFlowChart();

    const queryCache = client.getQueryCache();

    const queries = queryCache.findAll({
      queryKey: baseQueryKey,
    });

    let timeout;

    for (const query of queries) {
      if (query?.queryKey?.length === 5 && query.queryKey[4] === section?.id) {
        if (timeout) clearTimeout(timeout);

        setRefreshDisabled(true);
        setRefreshing(true);
        try {
          await client.refetchQueries({ queryKey: query.queryKey });
        } catch {}

        timeout = setTimeout(() => setRefreshDisabled(false), 5000);
      }
    }

    setRefreshing(false);
  }
};
